import { Box, IconButton, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileClient, FileModel, SearchFilesRequest } from "../../../api/api";
import IconComponent from "../../../common/components/IconComponent";
import { formatDate } from "../../../common/formatDate";
import { formatSize } from "../../../common/formatSize";
import { useApiClient, useErrorHandler } from "../../../common/hooks";
import { FileTableModel } from "../../Models/FileTableModel";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";
import TableCustomFooter from "../Table/TableCustomFooter";

const fieldNameMap: Record<string, string> = {
  name: "Name",
  size: "Size",
  contentType: "ContentType",
  create: "CreatedDate",
};

const pageSizeOptions = [25, 50];

const FileTableComponent: React.FC<IComponentProps<FileTableModel>> = ({ component, context }) => {
  const errorHandler = useErrorHandler();
  const fileClient = useApiClient(FileClient);

  const [rows, setRows] = useState<FileModel[]>([]);
  const [rowsPending, setRowsPending] = useState<boolean>(false);
  const [rowCount, setRowCount] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: pageSizeOptions[0] });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "name", sort: "asc" }]);
  const [searchAttributes, setSearchAttributes] = useState<{ [key: string]: string }>({});

  const handleRefresh = async () => {
    if (!component.GetSearchAttributes) {
      console.warn("GetSearchAttributes expression is not defined.");
      return;
    }

    setRowsPending(true);
    try {
      // Evaluate the GetSearchAttributes expression to get the search attributes
      const attributes = await context.Evaluator.evaluate(component.GetSearchAttributes, context);
      setSearchAttributes(attributes || {});

      // Don't make the search call if no attributes are provided
      if (!attributes || Object.keys(attributes).length === 0) {
        setRows([]);
        setRowCount(0);
        return;
      }

      const sortField = sortModel.length > 0 ? sortModel[0].field : undefined;
      const sortAscending = sortModel.length > 0 ? sortModel[0].sort === "asc" : true;

      const searchRequest: SearchFilesRequest = {
        attributes: attributes || {},
        pageIndex: paginationModel.page,
        pageSize: paginationModel.pageSize,
        sortBy: sortField ? fieldNameMap[sortField] : undefined,
        sortAscending: sortAscending,
      };

      const result = await fileClient.search(component.Id, searchRequest);

      if (result) {
        setRows(result.items || []);
        const totalCount = result.hasNextPage
          ? (paginationModel.page + 2) * paginationModel.pageSize
          : paginationModel.page * paginationModel.pageSize + (result.items?.length || 0);
        setRowCount(totalCount);
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setRowsPending(false);
    }
  };

  const handleOpenFile = useCallback((url: string) => {
    window.open(url, "_blank");
  }, []);

  const handleDownloadFile = useCallback((url: string, filename: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
  }, []);

  const handlePaginationModelChange = useCallback(
    (newModel: { page: number; pageSize: number }) => {
      // Reset page index to 0 when page size changes
      if (newModel.pageSize !== paginationModel.pageSize) {
        setPaginationModel({ page: 0, pageSize: newModel.pageSize });
      } else {
        setPaginationModel(newModel);
      }
    },
    [paginationModel.pageSize]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        sortable: true,
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <Link to={params.row.url} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "none" }}>
            {params.value}
          </Link>
        ),
      },
      {
        field: "created",
        headerName: "Created",
        sortable: true,
        width: 150,
        valueFormatter: (value: string) => formatDate(value),
      },
      {
        field: "contentType",
        headerName: "Content Type",
        sortable: true,
        width: 150,
      },
      {
        field: "size",
        headerName: "Size",
        sortable: true,
        width: 120,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number) => formatSize(value),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButton onClick={() => handleDownloadFile(params.row.url, params.row.name)} title="Download file" size="small">
              <IconComponent iconName="Download" />
            </IconButton>
            <IconButton onClick={() => handleOpenFile(params.row.url)} title="Open in new tab" size="small">
              <IconComponent iconName="OpenInNew" />
            </IconButton>
          </>
        ),
      },
    ],
    [handleOpenFile, handleDownloadFile]
  );

  const getRowId = useCallback((row: FileModel) => row.id, []);

  // Load data on start and when dependencies change
  useEffect(() => {
    handleRefresh();
  }, [component.GetSearchAttributes, paginationModel, sortModel]);

  const componentLayoutSx = getComponentLayout(component);

  const NoRowsOverlay = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        gap: 2,
      }}
    >
      <Box sx={{ opacity: 0.3 }}>
        <IconComponent iconName="Folder" fontSize={80} />
      </Box>
      <Typography variant="body1" color="text.secondary">
        No files found
      </Typography>
    </Box>
  );

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      getRowId={getRowId}
      loading={rowsPending}
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      sortingMode="server"
      pageSizeOptions={pageSizeOptions}
      rowCount={rowCount}
      paginationMode="server"
      disableRowSelectionOnClick
      slots={{
        footer: () => <TableCustomFooter onRefresh={handleRefresh} isLoading={rowsPending} />,
        noRowsOverlay: NoRowsOverlay,
      }}
      sx={{ ...componentLayoutSx, width: "100%", flex: 1, overflow: "auto" }}
    />
  );
};

export default FileTableComponent;
